import React, { useEffect, useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Legend } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles, chartColors } from "../../../../components/colors";

const barColorAnbieter = chartColors.primary[0];      // e.g. colors.lichtBlau
const barColorEmpfaenger = chartColors.alternative[0]; // e.g. colors.primary (PH Grün), swap as needed

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 880,
    maxWidth: 880,
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 420,
    boxSizing: "border-box"
};

const TOTAL = 20; // max sum in Ultimatum

function getLabels(language: string) {
    if (language === "en") {
        return {
            anbieter: "Proposer (offer kept)",
            empfaenger: "Recipient (minimum accepted)",
            xAxis: "Offer (proposer) / Minimum (recipient)",
            yourClass: "Your class",
            allClasses: "All classes",
            loading: "Loading global results…",
            title: "Ultimatum experiment – Global results"
        };
    } else {
        return {
            anbieter: "Anbieter:in (behaltenes Angebot)",
            empfaenger: "Empfänger:in (Mindestakzeptanz)",
            xAxis: "Angebot (Anbieter:in) / Mindestbetrag (Empfänger:in)",
            yourClass: "Deine Klasse",
            allClasses: "Alle Klassen",
            loading: "Lade globale Ergebnisse…",
            title: "Ultimatum-Experiment – Globale Ergebnisse"
        };
    }
}

function buildChartData(dist0: Record<string, number>, dist1: Record<string, number>, labels: any) {
    // keys: 0…20 for both
    return Array.from({ length: 21 }, (_, amt) => ({
        name: `${amt} / ${TOTAL - amt}`,
        [labels.anbieter]: dist0?.[amt] || 0,
        [labels.empfaenger]: dist1?.[amt] || 0,
    }));
}

// Custom Tooltip for both distributions
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
        return (
            <div style={{
                background: "#fff",
                border: `1px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                {payload.map((p: any) => {
                    const perc = total > 0 ? Math.round((p.value / total) * 100) : 0;
                    return (
                        <div key={p.dataKey} style={{ color: p.color || colors.textMedium }}>
                            {p.name}: {p.value} ({perc}%)
                        </div>
                    );
                })}
            </div>
        );
    }
    return null;
};

// Label only if > 0, centered over bar
const OnlyNonZeroLabel = (props: any) => {
    const { value, x, y, width, fill } = props;
    if (!value || value === 0) return null;
    return (
        <text
            x={x + (width ? width / 2 : 0)}
            y={y}
            dy={-8}
            fill={fill || "#222"}
            fontSize={15}
            textAnchor="middle"
            fontWeight="bold"
            style={{ pointerEvents: "none" }}
        >
            {value}
        </text>
    );
};

export const ResultScreenGlobalComponent = () => {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    const labels = useMemo(() => getLabels(language), [language]);
    const [dataOwnGroup, setDataOwnGroup] = useState<any[]>([]);
    const [globalUltimatum, setGlobalUltimatum] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingGlobal, setLoadingGlobal] = useState(true);

    // Helper to normalize group names
    const eigeneGruppe = userData?.gruppe ?? "";
    const normalizeGroup = (name: string | undefined | null) => (name ?? "").trim().toLowerCase();

    // Build local/class data ("your group")
    useEffect(() => {
        setLoading(true);
        getGroupData().then(allData => {
            // Build classwise distributions
            const anbieterDist: Record<number, number> = {}; // amountForSelf (Bedingung 1)
            const empfaengerDist: Record<number, number> = {}; // minAcceptableAmount (Bedingung 2)
            for (let i = 0; i <= 20; i++) { anbieterDist[i] = 0; empfaengerDist[i] = 0; }

            const userGroupNorm = normalizeGroup(eigeneGruppe);

            allData?.forEach((dat: any) => {
                const gruppe = dat.gruppe;
                const gruppeNorm = normalizeGroup(gruppe);
                if (gruppeNorm !== userGroupNorm) return; // Only own group

                // Use user's Bedingung for this record
                const bedingung = dat?.randomizer3;
                (dat?.Ultimatum?.["0"]?.selections ?? []).forEach((sel: any) => {
                    if (bedingung === "Bedingung 1" && typeof sel.amountForSelf === "number" && sel.amountForSelf >= 0 && sel.amountForSelf <= 20) {
                        anbieterDist[sel.amountForSelf] += 1;
                    }
                    if (bedingung === "Bedingung 2" && typeof sel.minAcceptableAmount === "number" && sel.minAcceptableAmount >= 0 && sel.minAcceptableAmount <= 20) {
                        empfaengerDist[sel.minAcceptableAmount] += 1;
                    }
                });
            });
            setDataOwnGroup(buildChartData(anbieterDist, empfaengerDist, labels));
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            console.error("Error fetching group data:", error);
        });
    }, [getGroupData, eigeneGruppe, labels]);

    // Load global aggregate Ultimatum data
    useEffect(() => {
        setLoadingGlobal(true);
        fetch("/api/aggregateGlobalData")
            .then(res => res.json())
            .then((apiJson) => {
                const ULTI = apiJson?.data?.Ultimatum;
                // ULTI: { "0": {0:count...20:count}, "1": {0...20} }
                setGlobalUltimatum(buildChartData(ULTI?.["0"], ULTI?.["1"], labels));
                setLoadingGlobal(false);
            }).catch(() => {
            setGlobalUltimatum([]);
            setLoadingGlobal(false);
        });
    }, [labels]);

    const thinBarSize = 20;

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>{labels.loading}</div>;
    }

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {labels.title}
            </h2>
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 48,
                marginTop: 24,
                width: "100%"
            }}>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {labels.yourClass}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        <ResponsiveContainer width="100%" height={360}>
                            <BarChart
                                data={dataOwnGroup}
                                margin={{ top: 30, right: 20, left: 10, bottom: 60 }}
                            >
                                <XAxis
                                    dataKey="name"
                                    interval={0}
                                    label={{ value: labels.xAxis, position: "insideBottom", offset: -36, fontSize: 14 }}
                                    tick={{ fontSize: 13, angle: -30, textAnchor: "end" } as any}
                                    height={90}
                                />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey={labels.anbieter} fill={barColorAnbieter} name={labels.anbieter} barSize={thinBarSize}>
                                    <LabelList dataKey={labels.anbieter} content={OnlyNonZeroLabel} />
                                </Bar>
                                <Bar dataKey={labels.empfaenger} fill={barColorEmpfaenger} name={labels.empfaenger} barSize={thinBarSize}>
                                    <LabelList dataKey={labels.empfaenger} content={OnlyNonZeroLabel} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {labels.allClasses}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        {loadingGlobal ? (
                            <div style={{ color: colors.brillantBlue, fontSize: 17, textAlign: "center", marginTop: 40 }}>
                                {labels.loading}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={360}>
                                <BarChart
                                    data={globalUltimatum}
                                    margin={{ top: 30, right: 20, left: 10, bottom: 60 }}
                                >
                                    <XAxis
                                        dataKey="name"
                                        interval={0}
                                        label={{ value: labels.xAxis, position: "insideBottom", offset: -36, fontSize: 14 }}
                                        tick={{ fontSize: 13, angle: -30, textAnchor: "end" } as any}
                                        height={90}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey={labels.anbieter} fill={barColorAnbieter} name={labels.anbieter} barSize={thinBarSize}>
                                        <LabelList dataKey={labels.anbieter} content={OnlyNonZeroLabel} />
                                    </Bar>
                                    <Bar dataKey={labels.empfaenger} fill={barColorEmpfaenger} name={labels.empfaenger} barSize={thinBarSize}>
                                        <LabelList dataKey={labels.empfaenger} content={OnlyNonZeroLabel} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ResultScreenGlobal: ScreenConfig = {
    component: ResultScreenGlobalComponent,
    defaultResult: {
        selections: [],
    },
    canContinueDefault: true,
};